import { useMemo, useState } from 'react';
import {
  Bell, Settings, LogOut, Moon, Search, Calendar, Filter,
  Check, RefreshCcw, CreditCard, Users, ChevronDown, User, Headphones, Menu,Sun
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../shared/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTitle } from '../../../shared/ui/sheet';
import { Popover, PopoverTrigger } from '../../../shared/ui/popover';
import { Button } from '../../../shared/ui/button';
import { Input } from '../../../shared/ui/input';
import { NotificationPanel } from '../../../features/notifications/components';
import { useAuth } from '../../../features/auth/hooks/useAuth';
import { useAuthStore } from '../../../features/auth/store/authStore';
import { useUserStore } from '../../../features/auth/store/userStore';
import { useUIStore } from '../../../shared/store/uiStore';
import { getFirmName, getSiteName } from '../../../shared/utils/firmName';
import { ProfileModal } from '../../../features/profile/components';
import leimbergLogo from '../../../assets/logo.svg';
import leimbergLogoDark from '../../../assets/logo-dark.svg';

// The notification panel is built to the artboard, but its rows are static sample content and its
// three actions are inert — there is no notifications endpoint yet (notificationService covers the
// preference toggles only). It stays mounted behind this flag rather than held in comments, so the
// feature switches on from one place once the API lands.
//
// Deliberately a const and not a VITE_ env var: the panel renders plausible-looking fake data
// (a ticket number, sender names, a renewal date), and an env var is exactly how that gets turned
// on in a deployed environment by someone who does not know it is mock.
//
// The unread dot rides the same flag. An inert bell must never advertise notifications that
// nobody can open, and tying both to one constant keeps them from drifting apart again.
const NOTIFICATIONS_ENABLED = false;

export function AppHeader({ showMenuButton = false, onThemeToggle, onMobileMenuToggle }) {
  const { logout } = useAuth();
  // Read only to label the toggle and pick the logo — colours come from tokens.
  const { isDarkMode } = useUIStore();
  const ThemeIcon = isDarkMode ? Sun : Moon;
  const { profile } = useUserStore();
  // Active site label — shown as a sub-line in the user dropdown so multi-site users can see
  // which site their actions are scoped to. Hidden entirely for single-site customers (which the
  // active customer's `sites` list on the auth store tells us) so the badge is noise-free.
  const selectedSite = useAuthStore((s) => s.selectedSite);
  const selectedCompanySites = useAuthStore((s) => s.selectedCompany?.sites);
  const hasMultipleSites = Array.isArray(selectedCompanySites) && selectedCompanySites.length > 1;
  // Firm count from the sidebar's `/api/firm` load. Null until it lands (or if it fails), which reads
  // as "not multi-firm" so a single-firm user never sees the company label flash in and out.
  const companyCount = useAuthStore((s) => s.companyCount);
  const hasMultipleCompanies = typeof companyCount === 'number' && companyCount > 1;

  const bgColor = 'bg-header-bg';
  const borderColor = 'border-header-border';
  const textColor = 'text-header-fg';
  const iconColor = 'text-header-icon';

  // isNotificationsOpen drives the older filtered Sheet below, which the Popover panel supersedes.
  // Nothing opens it, but it is left wired rather than deleted so the Sheet can be switched back on
  // from one place.
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isNotifPanelOpen, setIsNotifPanelOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [notifTab, setNotifTab] = useState('all');
  const [notifQuery, setNotifQuery] = useState('');
  const [notifications, setNotifications] = useState([
    { id: 'n-1', title: 'Payment failed', description: 'A recent payment attempt could not be processed.', createdAtISO: '2026-02-10T10:15:00Z', read: false },
    { id: 'n-2', title: 'Seats available', description: 'You have available seats that can be assigned.', createdAtISO: '2026-02-09T08:25:00Z', read: true },
    { id: 'n-3', title: 'Subscription updated', description: 'Your subscription changes have been saved.', createdAtISO: '2026-02-08T18:40:00Z', read: false },
    { id: 'n-4', title: 'New user invited', description: 'An invite was sent to a new user.', createdAtISO: '2026-02-07T11:05:00Z', read: true },
  ]);

  const unreadCount = useMemo(() => notifications.filter((n) => !n.read).length, [notifications]);

  const filteredNotifs = useMemo(() => {
    const q = notifQuery.trim().toLowerCase();
    return notifications
      .filter((n) => notifTab === 'unread' ? !n.read : true)
      .filter((n) => !q || n.title.toLowerCase().includes(q) || n.description.toLowerCase().includes(q))
      .sort((a, b) => Date.parse(b.createdAtISO) - Date.parse(a.createdAtISO));
  }, [notifications, notifTab, notifQuery]);

  const unreadSection = filteredNotifs.filter((n) => !n.read);
  const earlierSection = filteredNotifs.filter((n) => n.read);

  const formatTime = (iso) => {
    const diff = Date.now() - Date.parse(iso);
    const day = 86400000;
    if (diff < day) return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    if (diff < 2 * day) return 'Yesterday';
    return `${Math.floor(diff / day)} days ago`;
  };

  const iconForNotif = (title) => {
    const t = title.toLowerCase();
    if (t.includes('payment')) return CreditCard;
    if (t.includes('subscription')) return RefreshCcw;
    if (t.includes('seat')) return Users;
    return Bell;
  };

  // Get user data from profile (me API response)
  const firstName = profile?.personalInfo?.firstName || '';
  const lastName = profile?.personalInfo?.lastName || '';
  const email = profile?.personalInfo?.email || '';
  const currentFirmName = getFirmName(profile);
  const currentSiteName = hasMultipleSites ? getSiteName(selectedSite, profile) : '';
  // Header scope label: each part appears only when the user actually has a choice of it.
  //   1 firm,  1 site   → nothing
  //   1 firm,  N sites  → site only
  //   N firms, N sites  → firm + site
  //   N firms, 1 site   → firm only
  const scopeFirmName = hasMultipleCompanies ? currentFirmName : '';
  const scopeSiteName = currentSiteName;
  const siteLineClass = scopeFirmName ? 'text-xs text-fg-muted' : `text-sm font-semibold ${textColor}`;

  const initials = firstName && lastName
    ? `${firstName[0]}${lastName[0]}`.toUpperCase()
    : 'UN';
  const fullName = firstName && lastName ? `${firstName} ${lastName}` : 'User Name';

  // Extracted so the two branches below can share one button rather than duplicating its markup:
  // with the flag off it renders bare, with the flag on the Popover wraps this exact element.
  const bell = (
    <button
      type="button"
      className={`p-2 sm:p-2.5 rounded-full bg-header-bell border border-chrome-edge transition-colors cursor-pointer relative flex-shrink-0`}
      aria-label="Notifications"
      data-testid="app-header-notifications-bell"
    >
      <Bell className={`w-4 h-4 sm:w-5 sm:h-5 ${iconColor}`} />
      {NOTIFICATIONS_ENABLED && unreadCount > 0 && (
        <span className="absolute top-1.5 sm:top-2 right-2 sm:right-2.5 w-2.5 sm:w-3 h-2.5 sm:h-3 bg-brand rounded-full" data-testid="app-header-notifications-unread-dot" />
      )}
    </button>
  );

  return (
    <header className={`h-16 border-b ${borderColor} ${bgColor} flex items-center justify-between pr-2 sm:pr-6 pl-2 sticky top-0 z-30`}  data-testid="app-header">
      {/* Left: hamburger menu (mobile) + logo */}
      <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
        {/* Opens the nav drawer. Shown only where the layout uses a drawer rather
            than the persistent rail — the layout owns that decision, not a CSS
            breakpoint here, so the two can never disagree. */}
        {showMenuButton && (
          <button
            type="button"
            onClick={onMobileMenuToggle}
            // Same blue as the avatar (bg-nav-item-active, white glyph), so the two header ends match.
            className="p-2 rounded-lg bg-nav-item-active border border-chrome-edge transition-colors cursor-pointer flex-shrink-0"
            aria-label="Open menu"
            data-testid="app-header-mobile-menu-button"
          >
            <Menu className="w-5 h-5 text-white" />
          </button>
        )}
        
        <img
          // The mark is dark ink on transparent, so it needs the light-ink
          // variant in dark mode rather than a CSS filter.
          src={isDarkMode ? leimbergLogoDark : leimbergLogo}
          alt="Leimberg, LeClair & Lackner, Inc."
          className="h-auto max-w-[180px] sm:max-w-full"
          style={{ maxHeight: '20px' }}
          data-testid="app-header-logo"
        />
      </div>

      {/* Right: current company/site label + bell + avatar */}
      <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
        {/* Read-only echo of the sidebar FirmSelector's active selection, so the scope stays visible
            when the rail is collapsed. Switching still happens in the sidebar. Which parts show is
            decided by scopeFirmName / scopeSiteName above. */}
        {(scopeFirmName || scopeSiteName) && (
          <div
            className="hidden sm:flex flex-col items-end leading-tight max-w-[220px] mr-1 min-w-0"
            title={[scopeFirmName, scopeSiteName && `Site: ${scopeSiteName}`].filter(Boolean).join('\n')}
            data-testid="app-header-current-scope"
          >
            {scopeFirmName && (
              <span className={`text-sm font-semibold truncate max-w-full ${textColor}`} data-testid="app-header-current-firm">{scopeFirmName}</span>
            )}
            {scopeSiteName && (
              // Alone, the site line takes the primary style so the label doesn't look like a
              // sub-line with its heading missing.
              <span
                className={`truncate max-w-full ${siteLineClass}`}
                data-testid="app-header-current-site"
              >
                Site: {scopeSiteName}
              </span>
            )}
          </div>
        )}

        {/* Bell. While NOTIFICATIONS_ENABLED is false the bell renders on its own and opens
            nothing — and, because the dot rides the same flag, it shows no unread marker either. */}
        {NOTIFICATIONS_ENABLED ? (
          <Popover open={isNotifPanelOpen} onOpenChange={setIsNotifPanelOpen}>
            <PopoverTrigger asChild>{bell}</PopoverTrigger>
            <NotificationPanel />
          </Popover>
        ) : (
          bell
        )}

        {/* Avatar dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center gap-1 sm:gap-2 hover:bg-header-hover rounded-full pl-1 pr-1 sm:pr-2 py-1 transition-colors cursor-pointer flex-shrink-0"
              data-testid="app-header-user-dropdown-trigger"
            >
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-nav-item-active flex items-center justify-center text-white font-bold text-sm sm:text-base flex-shrink-0" data-testid="app-header-user-initials">
                {initials}
              </div>
              <div className="hidden sm:flex flex-col items-start leading-tight">
                <span className={`text-sm font-bold ${textColor}`} data-testid="app-header-user-fullname">{fullName}</span>
              </div>
              <ChevronDown className='hidden sm:block w-5 h-5 text-brand font-bold' />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className={`w-64 ${bgColor} ${textColor} border ${borderColor} shadow-xl rounded-xl p-2`} data-testid="app-header-user-dropdown-content">
            {/* User Info Section */}
            <div className="px-3 py-3">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-nav-item-active flex items-center justify-center text-white font-bold text-base flex-shrink-0">
                  {initials}
                </div>
                <div className="min-w-0 flex-1">
                  <div className={`font-bold text-fg truncate`} data-testid="app-header-user-dropdown-fullname">{fullName}</div>
                  <div className={`text-xs truncate text-fg-secondary`} data-testid="app-header-user-dropdown-email">{email}</div>
                  {currentFirmName && (
                    <div className={`text-xs truncate text-brand font-medium mt-0.5`} data-testid="app-header-user-dropdown-firm">{currentFirmName}</div>
                  )}
                  {currentSiteName && (
                    <div className={`text-xs truncate text-fg-muted mt-0.5`} data-testid="app-header-user-dropdown-site">Site: {currentSiteName}</div>
                  )}
                </div>
              </div>
            </div>
            <DropdownMenuSeparator className="bg-line my-1" />
            
            {/* Menu Items */}
            <DropdownMenuItem
              className={`cursor-pointer flex items-center gap-3 px-3 py-2.5 hover:bg-header-hover rounded-lg font-medium text-fg`}
              onClick={() => setIsSettingsOpen(true)}
              data-testid="app-header-user-dropdown-profile"
            >
              <User className="w-4 h-4 text-header-icon" />
              <span>Profile</span>
            </DropdownMenuItem>
            {/* Hidden on request. Kept rather than deleted so it can be switched back on by
                uncommenting. Note it opened the very same ProfileModal as "Profile" above, so
                while it is hidden nothing is unreachable — the menu simply stops offering one
                destination under two names.
            <DropdownMenuItem
              className={`cursor-pointer flex items-center gap-3 px-3 py-2.5 hover:bg-header-hover rounded-lg font-medium text-fg`}
              onClick={() => setIsSettingsOpen(true)}
              data-testid="app-header-user-dropdown-settings"
            >
              <Settings className="w-4 h-4 text-header-icon" />
              <span>Settings</span>
            </DropdownMenuItem>
            */}
            <DropdownMenuItem
              onClick={onThemeToggle}
              className={`cursor-pointer flex items-center gap-3 px-3 py-2.5 hover:bg-header-hover rounded-lg font-medium text-fg`}
              data-testid="app-header-user-dropdown-theme"
            >
              {/* Names the mode this will switch *to*, not the one already on screen. */}
              <ThemeIcon className="w-4 h-4 text-header-icon" />
              <span>{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className={`cursor-pointer flex items-center gap-3 px-3 py-2.5 hover:bg-header-hover rounded-lg font-medium text-fg`}
              data-testid="app-header-user-dropdown-support"
            >
              <Headphones className="w-4 h-4 text-header-icon" />
              <span>Contact Support</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-line my-1" />
            <DropdownMenuItem
              onClick={() => logout()}
              className={`cursor-pointer flex items-center gap-3 px-3 py-2.5 text-header-danger hover:bg-header-hover rounded-lg font-medium`}
              data-testid="app-header-user-dropdown-logout"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Notifications Sheet */}
      <Sheet open={isNotificationsOpen} onOpenChange={setIsNotificationsOpen}>
        <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto p-0" data-testid="app-header-notifications-sheet">
          <div className="border-b border-header-border p-4 pr-20">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-header-fg flex items-center justify-center">
                <Bell className="w-5 h-5 text-white" />
              </div>
              <div className="flex items-center gap-3">
                <SheetTitle data-testid="app-header-notifications-title">Notifications</SheetTitle>
                <span className="inline-flex items-center rounded-full bg-header-badge px-2 py-0.5 text-xs font-semibold text-header-fg" data-testid="app-header-notifications-unread-count">
                  {unreadCount} unread
                </span>
              </div>
            </div>
            <button
              type="button"
              className="absolute top-4 right-12 h-8 w-8 inline-flex items-center justify-center rounded-md border border-header-border bg-header-bg text-header-fg hover:bg-header-hover"
              aria-label="Notification settings"
              data-testid="app-header-notifications-settings-button"
            >
              <Settings className="w-4 h-4" />
            </button>
          </div>

          <div className="p-4 space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-header-fg-muted" />
                <Input
                  value={notifQuery}
                  onChange={(e) => setNotifQuery(e.target.value)}
                  placeholder="Search notifications..."
                  className="h-9 pl-9 bg-header-hover border-header-border text-header-fg placeholder:text-fg-placeholder"
                  data-testid="app-header-notifications-search-input"
                />
              </div>
              <button type="button" className="h-9 px-3 inline-flex items-center gap-2 rounded-md border border-header-border bg-header-bg text-sm font-medium text-header-fg" data-testid="app-header-notifications-time-filter">
                <Calendar className="w-4 h-4 text-header-fg-muted" /> Any time
              </button>
              <button type="button" className="h-9 px-3 inline-flex items-center gap-2 rounded-md border border-header-border bg-header-bg text-sm font-medium text-header-fg" data-testid="app-header-notifications-type-filter">
                <Filter className="w-4 h-4 text-header-fg-muted" /> All types
              </button>
            </div>

            <div className="flex items-center justify-between gap-3 border-b border-header-border">
              <div className="flex items-center gap-5">
                {['all', 'unread'].map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setNotifTab(tab)}
                    className={`pb-3 text-sm font-semibold capitalize ${
                      notifTab === tab ? 'text-header-fg border-b-2 border-header-fg' : 'text-header-fg-muted'
                    }`}
                    data-testid={`app-header-notifications-tab-${tab}`}
                  >
                    {tab === 'all' ? 'All' : 'Unread'}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="pb-3 text-xs font-semibold text-header-fg inline-flex items-center gap-2"
                onClick={() => setNotifications((prev) => prev.map((n) => ({ ...n, read: true })))}
                data-testid="app-header-notifications-mark-all-read"
              >
                <Check className="w-4 h-4 text-header-fg-muted" /> Mark all as read
              </button>
            </div>
          </div>

          <div className="px-4 pb-4 space-y-4" data-testid="app-header-notifications-list">
            {unreadSection.length > 0 && (
              <div className="space-y-3" data-testid="app-header-notifications-section-unread">
                <div className="text-xs font-bold text-header-fg-body uppercase tracking-wide">Unread</div>
                <div className="divide-y divide-header-border rounded-md border border-header-border bg-header-bg">
                  {unreadSection.map((n) => {
                    const Icon = iconForNotif(n.title);
                    return (
                      <div key={n.id} className="p-4" data-testid={`app-header-notification-item-${n.id}`}>
                        <div className="flex gap-3">
                          <div className="pt-2"><span className="inline-block h-2 w-2 rounded-full bg-header-fg" /></div>
                          <div className={`h-9 w-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                            n.title.toLowerCase().includes('payment') ? 'bg-danger-bg text-danger-fg' : 'bg-header-badge text-header-fg'
                          }`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-3">
                              <div className="text-sm font-semibold text-header-fg truncate">{n.title}</div>
                              <div className="text-xs text-header-fg-muted flex-shrink-0">{formatTime(n.createdAtISO)}</div>
                            </div>
                            <div className="text-xs text-header-fg-body mt-1">{n.description}</div>
                            {n.title.toLowerCase().includes('payment') && (
                              <div className="mt-3 flex flex-wrap gap-2">
                                <Button type="button" className="h-8" data-testid={`app-header-notification-action-payment-${n.id}`}>Update payment method</Button>
                                <Button type="button" variant="secondary" className="h-8" data-testid={`app-header-notification-action-details-${n.id}`}>View details</Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {earlierSection.length > 0 && (
              <div className="space-y-3" data-testid="app-header-notifications-section-earlier">
                <div className="text-xs font-bold text-header-fg-body uppercase tracking-wide">Earlier</div>
                <div className="divide-y divide-header-border rounded-md border border-header-border bg-header-bg">
                  {earlierSection.map((n) => {
                    const Icon = iconForNotif(n.title);
                    return (
                      <div key={n.id} className="p-4" data-testid={`app-header-notification-item-${n.id}`}>
                        <div className="flex gap-3">
                          <div className="pt-2"><span className="inline-block h-2 w-2 rounded-full bg-transparent" /></div>
                          <div className="h-9 w-9 rounded-full bg-header-hover text-header-fg flex items-center justify-center flex-shrink-0">
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-3">
                              <div className="text-sm font-semibold text-header-fg truncate">{n.title}</div>
                              <div className="text-xs text-header-fg-muted flex-shrink-0">{formatTime(n.createdAtISO)}</div>
                            </div>
                            <div className="text-xs text-header-fg-body mt-1">{n.description}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Profile Modal */}
      <ProfileModal open={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} dataTestId="header-profile-modal" />
    </header>
  );
}
